import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { FUNDER_NAV } from "./FunderTerminal";
import TrialBanner from "../components/TrialBanner";
import { BellRing, Check, Settings, TrendingUp } from "lucide-react";

export default function FunderAlerts() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ alerts_enabled: true, alert_min_compat: 85 });
  const [editing, setEditing] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const [a, s] = await Promise.all([api.get("/funder/alerts"), api.get("/funder/alerts/settings")]);
    setRows(a.data); setSettings(s.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    await api.post(`/funder/alerts/${id}/read`);
    setRows(r => r.map(x => x.alert_id === id ? {...x, read: true} : x));
  };
  const markAllRead = async () => {
    await api.post("/funder/alerts/read-all");
    setRows(r => r.map(x => ({...x, read: true})));
  };
  const saveSettings = async () => {
    const { data } = await api.put("/funder/alerts/settings", settings);
    setSettings(data); setEditing(false);
    setMsg("Settings saved."); setTimeout(()=>setMsg(""), 2500);
  };

  const unread = rows.filter(r => !r.read).length;

  return (
    <AppShell nav={FUNDER_NAV}>
      <TrialBanner />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-neutral-900">Smart alerts</h1>
            {unread > 0 && <span data-testid="alerts-page-unread" className="rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-bold text-white">{unread} new</span>}
          </div>
          <p className="mt-1 text-sm text-neutral-600">
            We ping you the moment a newly verified SME hits your mandate-fit threshold.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAllRead}
              className="rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
              <Check className="mr-1 inline h-3 w-3"/> Mark all read
            </button>
          )}
          <button data-testid="open-alert-settings" onClick={()=>setEditing(true)}
            className="rounded-lg bg-[#0A8B85] px-3 py-2 text-xs font-semibold text-white hover:bg-[#066667]">
            <Settings className="mr-1 inline h-3 w-3"/> Settings
          </button>
        </div>
      </div>

      {msg && <p className="mt-4 rounded-lg bg-[#0A8B85]/10 px-4 py-2 text-sm text-[#0A8B85]" data-testid="alerts-msg">{msg}</p>}

      {/* Status strip */}
      <div className="mt-6 rounded-2xl border border-neutral-200 bg-white p-4 text-sm text-neutral-600 flex items-center gap-3" data-testid="alerts-status">
        <BellRing className="h-4 w-4 text-[#0A8B85]"/>
        Alerts are <b className={settings.alerts_enabled ? "text-[#0A8B85]" : "text-red-600"}>{settings.alerts_enabled ? "on" : "off"}</b>.
        Minimum compatibility: <b>{settings.alert_min_compat}</b>.
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid="alerts-table">
        {loading ? <p className="p-8 text-center text-sm text-neutral-500">Loading…</p> :
          rows.length === 0 ? <p className="p-12 text-center text-sm text-neutral-500">No alerts yet. As founders verify, we'll notify you here.</p> :
          rows.map(a => (
            <div key={a.alert_id} data-testid={`alert-${a.alert_id}`}
              className={"flex items-start gap-4 border-t border-neutral-100 px-5 py-4 first:border-t-0 " + (a.read ? "" : "bg-[#0A8B85]/[0.04]")}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0A8B85]/10 text-[#0A8B85]">
                <BellRing className="h-4 w-4"/>
              </div>
              <div className="flex-1">
                <div className="flex items-baseline gap-2 text-sm">
                  <p className="font-bold text-neutral-900">{a.company_name}</p>
                  {a.kind === "project" && (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700">Project</span>
                  )}
                  {!a.read && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">NEW</span>}
                </div>
                <p className="mt-0.5 text-xs text-neutral-500">
                  {a.reason === "project_scored" ? "New funder-ready project" : "Just verified"}
                  {" · "}{[a.sector, a.country].filter(Boolean).join(" · ")} · {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-1 text-[#0A8B85]">
                <TrendingUp className="h-4 w-4"/>
                <span className="font-extrabold">{a.compatibility}</span>
                <span className="text-[10px] uppercase tracking-wider text-neutral-400">fit</span>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`alert-view-${a.alert_id}`} onClick={()=>{ markRead(a.alert_id); nav(`/funder/sme/${a.company_id}`); }}
                  className="rounded-md bg-[#0A8B85] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#066667]">
                  View deal
                </button>
                {!a.read && (
                  <button data-testid={`alert-read-${a.alert_id}`} onClick={()=>markRead(a.alert_id)}
                    className="rounded-md border border-neutral-200 bg-white p-1.5 text-neutral-500 hover:bg-neutral-50">
                    <Check className="h-3.5 w-3.5"/>
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Settings modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="alert-settings-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-bold text-neutral-900">Alert settings</h2>
            <p className="mt-1 text-xs text-neutral-500">Decide when Clinq should ping you.</p>
            <div className="mt-5 space-y-5">
              <label className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 px-4 py-3 text-sm">
                <span><b className="block">Enable alerts</b><span className="text-xs text-neutral-500">Bell + email notifications.</span></span>
                <input data-testid="alerts-enabled" type="checkbox" checked={settings.alerts_enabled}
                  onChange={e=>setSettings({...settings, alerts_enabled: e.target.checked})}
                  className="h-4 w-4 accent-[#0A8B85]"/>
              </label>
              <div>
                <label className="flex items-baseline justify-between text-sm">
                  <span className="font-semibold">Minimum compatibility</span>
                  <span className="font-mono text-lg font-bold text-[#0A8B85]">{settings.alert_min_compat}</span>
                </label>
                <input data-testid="alerts-threshold" type="range" min={50} max={100} step={5}
                  value={settings.alert_min_compat}
                  onChange={e=>setSettings({...settings, alert_min_compat: +e.target.value})}
                  className="mt-2 w-full accent-[#0A8B85]"/>
                <p className="mt-1 text-[11px] text-neutral-500">Only SMEs whose mandate-fit ≥ this number will alert you.</p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={()=>setEditing(false)} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">Cancel</button>
              <button data-testid="alerts-save" onClick={saveSettings} className="rounded-lg bg-[#0A8B85] px-4 py-2 text-sm font-semibold text-white hover:bg-[#066667]">Save</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
